import { CircularProgress, Grow, Stack, Box, alpha, Button } from '@mui/material';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import { isOfferDisclaimerAvailable, MarketingContent } from '@ocx/data-offers';
import { SizingMultiplier, Typography, LoadingButton, LoadingButtonProps } from '@ocx/ui';

import {
  OfferDetailsAvailableAtLocation,
  OfferDetailsLocationsCarousel,
} from './offer-details-locations-carousel.component';
import { OfferDetailsMedia } from './offer-details-media.component';
import { OfferDetailsTermsAndDisclaimer } from './offer-details-terms-and-disclaimer.component';
import { OfferLocationsCarouselItemOnClickParams } from './offer-details-locations.types';
import { OfferIneligibilityWarning } from '../offer-ineligibility-warning/offer-ineligibility-warning.component';
import { SurgeonWarning } from '../surgeon-warning/surgeon-warning.component';

export type OfferDetailsAction = {
  title: MessageDescriptor;
  buttonVariant?: LoadingButtonProps['variant'];
  isExecuting?: boolean;
  onClick: () => unknown;
};

export type OfferDetailsProps = {
  isLoading?: boolean;
  marketingContent: MarketingContent;
  action?: OfferDetailsAction | null;
  variant?: 'default' | 'banner' | 'big-image';
  note?: string | null;
  ineligibility?: {
    message: string;
  };
  availableAtLocations?: OfferDetailsAvailableAtLocation[];
  hasLocationConstraints?: boolean;
  memberHasSharedLocation?: boolean;
  isLocalOffersEnabled?: boolean;
  onStoreIconClick?: (params: OfferLocationsCarouselItemOnClickParams) => void;
  onClose: () => void;
};

export const OfferDetails = ({
  isLoading = false,
  marketingContent,
  action,
  variant = 'default',
  note,
  ineligibility,
  availableAtLocations,
  hasLocationConstraints,
  memberHasSharedLocation,
  isLocalOffersEnabled,
  onStoreIconClick,
  onClose,
}: OfferDetailsProps) => {
  const {
    title,
    longDescription,
    imageUrl,
    shortDescription,
    disclaimer,
    disclaimerUrl,
    terms,
    termsUrl,
    surgeonGeneralsWarning,
  } = marketingContent;
  const showTermsAndDisclaimerSection = isOfferDisclaimerAvailable(marketingContent);
  const description = longDescription || shortDescription;

  return (
    <Stack
      data-testid="offer-details-popup"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      sx={(theme) => ({
        // Required to prevent loading overlay from exceeding border radius of the modal paper
        borderRadius: {
          xs: theme.spacing(SizingMultiplier.md, SizingMultiplier.md, 0, 0),
          sm: theme.spacing(SizingMultiplier.md),
        },
      })}>
      {surgeonGeneralsWarning && (
        <Box m={SizingMultiplier.lg} mb={SizingMultiplier.sm}>
          <SurgeonWarning square message={surgeonGeneralsWarning} size="large" />
        </Box>
      )}
      {imageUrl && <OfferDetailsMedia variant={variant} src={imageUrl} alt={title} />}
      <Stack
        spacing={2}
        pt={variant === 'banner' ? SizingMultiplier.lg : SizingMultiplier.xl}
        px={SizingMultiplier.lg}
        pb={SizingMultiplier.xl}>
        <div>
          <Typography data-testid="offer-title" variant="h6" gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography data-testid="offer-long-description" variant="body2" gutterBottom>
              {description}
            </Typography>
          )}
          {note && (
            <Typography variant="body2" color="text.secondary">
              {note}
            </Typography>
          )}
        </div>

        {isLocalOffersEnabled && (
          <OfferDetailsLocationsCarousel
            locations={availableAtLocations}
            hasLocationConstraints={hasLocationConstraints}
            memberHasSharedLocation={memberHasSharedLocation}
            onStoreIconClick={onStoreIconClick}
          />
        )}

        {showTermsAndDisclaimerSection && (
          <OfferDetailsTermsAndDisclaimer
            terms={terms}
            termsUrl={termsUrl}
            disclaimer={disclaimer}
            disclaimerUrl={disclaimerUrl}
          />
        )}
        {ineligibility && <OfferIneligibilityWarning message={ineligibility.message} />}
        <Grow in={!!action} appear unmountOnExit>
          <LoadingButton
            fullWidth
            variant={action?.buttonVariant}
            loading={action?.isExecuting || false}
            disabled={action?.isExecuting || isLoading || Boolean(ineligibility)}
            onClick={action?.onClick}>
            {!!action?.title && <FormattedMessage {...action.title} />}
          </LoadingButton>
        </Grow>

        <Button variant="text" onClick={onClose} sx={{ mt: SizingMultiplier.sm }}>
          <FormattedMessage
            id="offer-details-qr:close:button"
            defaultMessage="CLOSE"
            description="Close button for QR code modal"
          />
        </Button>
      </Stack>

      {isLoading && (
        <Stack
          position="absolute"
          height="100%"
          width="100%"
          justifyContent="center"
          alignItems="center"
          sx={(theme) => ({ backgroundColor: alpha(theme.palette.background.paper, 0.6) })}>
          <CircularProgress size={24} />
        </Stack>
      )}
    </Stack>
  );
};
